import React, { useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { LOGO_HORIZONTAL } from "./Logo";

/**
 * Premium mobile-first image carousel with arrows + swipe + dot indicators
 * and a subtle horizontal-logo watermark on every image.
 */
export default function ImageCarousel({ images, productName }) {
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);
  const touchStart = useRef(null);

  const n = images?.length || 0;
  const safeIdx = idx >= n ? 0 : idx;

  if (n === 0) {
    return (
      <div className="aspect-[4/5] bg-[#F2F0EA] border border-[#E8E6DF] rounded-sm flex items-center justify-center">
        <div className="text-[#5E6033] font-display text-6xl">{(productName || "H")[0]}</div>
      </div>
    );
  }

  const go = (dir) => {
    setIdx((i) => (i + dir + n) % n);
  };

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStart.current = null;
  };

  return (
    <div className="select-none">
      <div
        className="relative aspect-[4/5] bg-[#F2F0EA] border border-[#E8E6DF] rounded-sm overflow-hidden"
        ref={trackRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {images.map((img, i) => (
          <img
            key={img.id || i}
            src={fileUrl(img.storage_path)}
            alt={`${productName} ${i + 1}`}
            data-testid={`carousel-image-${i}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              i === safeIdx ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          />
        ))}

        {/* Watermark — bottom right, premium and visible */}
        <div className="pointer-events-none absolute bottom-3 right-3 md:bottom-4 md:right-4 z-10 bg-white/85 backdrop-blur-md border border-white/50 rounded-sm px-3 py-2 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
          <img
            src={LOGO_HORIZONTAL}
            alt=""
            className="h-7 md:h-9 w-auto"
            style={{ mixBlendMode: "multiply", opacity: 0.95 }}
          />
        </div>

        {/* Arrows */}
        {n > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              data-testid="carousel-prev-button"
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white border border-[#E8E6DF] rounded-full flex items-center justify-center shadow-md transition-all active:scale-95"
            >
              <ChevronLeft size={20} strokeWidth={2} className="text-[#1A1A18]" />
            </button>
            <button
              onClick={() => go(1)}
              data-testid="carousel-next-button"
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white border border-[#E8E6DF] rounded-full flex items-center justify-center shadow-md transition-all active:scale-95"
            >
              <ChevronRight size={20} strokeWidth={2} className="text-[#1A1A18]" />
            </button>
          </>
        )}

        {/* Counter pill */}
        {n > 1 && (
          <div className="absolute top-3 left-3 bg-[#1A1A18]/80 text-white text-[11px] font-medium tracking-wide px-2.5 py-1 rounded-sm">
            {safeIdx + 1} / {n}
          </div>
        )}
      </div>

      {/* Dot indicators */}
      {n > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`carousel-dot-${i}`}
              aria-label={`Go to image ${i + 1}`}
              className={`h-1.5 transition-all rounded-full ${
                i === safeIdx ? "bg-[#B5953B] w-6" : "bg-[#E8E6DF] w-1.5 hover:bg-[#B5953B]/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
